import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaTimes } from 'react-icons/fa'
import { useBusinessContext, useFeature, PermissionSet, usePermission } from '@context'
import { fetchHolviAccounts } from '@root/query/holvi'
import { updateBusiness } from '@root/query'
import { Button, GoalStepper, Prompt, TextTooltip } from '@root/components'
import { HolviAuthorizeButton } from '@root/containers/HolviAuthorizeButton/HolviAuthorizeButton'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'

interface PropsOut {
  isLoading: boolean
  showOnboarding: boolean
  nocfoConnectionMissing: boolean
  holviAccountsMissing: boolean
  holviAccountOwnerMissing: boolean
}

const BUSINESS_UPDATE_PERMISSIONS = [
  ...PermissionSet.BOOKKEEPING_ADMIN,
  ...PermissionSet.INVOICING_ADMIN
] as const

export const useShowHolviOnboarding = (): PropsOut => {
  const { enabled: holviIntegrationEnabled } = useFeature('holvi_integration')
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const { slug, data: business } = useBusinessContext()
  const { data: holviAccountData, isLoading: isAccountsLoading } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    enabled: Boolean(business?.slug) && holviIntegrationEnabled && canManageIntegrations
  })

  const accounts = holviAccountData?.results || []
  const nocfoConnectionMissing = !business?.holvi_nocfo_connection_enabled
  const holviAccountsMissing = accounts?.length === 0
  const holviAccountOwnerMissing =
    accounts.filter(({ account_owner }) => !!account_owner).length === 0

  return {
    isLoading: isAccountsLoading || !business.slug,
    nocfoConnectionMissing,
    holviAccountsMissing,
    holviAccountOwnerMissing,
    showOnboarding: nocfoConnectionMissing || holviAccountsMissing || holviAccountOwnerMissing
  }
}

export const HolviPaymentsOnboarding: React.FC = () => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [promptVisible, setPromptVisible] = useState(false)
  const canUpdateBusiness = usePermission(BUSINESS_UPDATE_PERMISSIONS)

  const { mutateAsync: setNocfoConnectionEnabled, isPending: isLoading } = useMutation({
    mutationFn: () => updateBusiness({ slug }, { holvi_nocfo_connection_enabled: true }),
    ...{
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug], exact: true })
      }
    }
  })

  const { holviAccountsMissing, nocfoConnectionMissing, holviAccountOwnerMissing } =
    useShowHolviOnboarding()

  const doneButton = (
    <Button
      intent="success"
      size="sm"
      icon={<FaCheck />}
      disabled={!canUpdateBusiness}
      onClick={() => canUpdateBusiness && setPromptVisible(true)}
    >
      {t('general.done')}
    </Button>
  )

  return (
    <>
      <Container>
        <Message>{t('containers.paymentFormModal.onboarding.message')}</Message>
        <GoalStepper
          goals={[
            {
              content:
                holviAccountOwnerMissing && !holviAccountsMissing ? (
                  <StepWrapper>
                    <h4>{t('containers.paymentFormModal.onboarding.updateHolvi.title')}</h4>
                    <p>{t('containers.paymentFormModal.onboarding.updateHolvi.description')}</p>
                    {holviAccountOwnerMissing && <>{<HolviAuthorizeButton size="sm" />}</>}
                  </StepWrapper>
                ) : (
                  <StepWrapper>
                    <h4>{t('containers.paymentFormModal.onboarding.createHolvi.title')}</h4>
                    <p>{t('containers.paymentFormModal.onboarding.createHolvi.description')}</p>
                    {holviAccountsMissing && <>{<HolviAuthorizeButton size="sm" />}</>}
                  </StepWrapper>
                ),
              isCompleted: !holviAccountsMissing && !holviAccountOwnerMissing
            },
            {
              content: (
                <StepWrapper>
                  <h4>{t('containers.paymentFormModal.onboarding.nocfoConnection.title')}</h4>
                  <ReactMarkdown
                    components={{
                      a: props => <a {...props} target="_blank" rel="noreferrer" />
                    }}
                  >
                    {t('containers.paymentFormModal.onboarding.nocfoConnection.description')}
                  </ReactMarkdown>
                  {nocfoConnectionMissing &&
                    (canUpdateBusiness ? (
                      doneButton
                    ) : (
                      <TextTooltip
                        tooltip={t(
                          'containers.paymentFormModal.onboarding.missingPermissionTooltip'
                        )}
                      >
                        <DisabledActionWrapper>{doneButton}</DisabledActionWrapper>
                      </TextTooltip>
                    ))}
                </StepWrapper>
              ),
              isCompleted: !nocfoConnectionMissing
            }
          ]}
        />
      </Container>

      <Prompt
        title={t('containers.paymentFormModal.onboarding.title')}
        description={t('containers.paymentFormModal.onboarding.prompt')}
        buttons={[
          {
            action: () => setPromptVisible(false),
            text: t('general.no'),
            icon: <FaTimes />
          },
          {
            action: () => setNocfoConnectionEnabled().then(() => setPromptVisible(false)),
            text: t('general.yes'),
            icon: <FaCheck />,
            intent: 'success',
            showSpinner: isLoading,
            disabled: !canUpdateBusiness
          }
        ]}
        isVisible={promptVisible}
        onClose={() => setPromptVisible(false)}
      />
    </>
  )
}

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-direction: column;
`

const Message = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
`

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  h4 {
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    margin: 0;
  }

  p {
    margin: 0;
  }

  & > * {
    width: fit-content;
  }
`

const DisabledActionWrapper = styled.span`
  display: inline-flex;
  cursor: not-allowed;
`
